import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from './axiosConfig';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  IconButton,
  Badge,
  Switch,
  FormControlLabel,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  LinearProgress
} from '@mui/material';
import {
  People,
  Schedule,
  AttachMoney,
  Inventory,
  Assessment,
  Settings,
  HelpOutline,
  TrendingUp,
  ArrowForward,
  CheckCircle,
  Notifications,
  Logout,
  AccountCircle,
  CalendarToday
} from '@mui/icons-material';

// Import the new components
import SundayPlanningInterface from './SundayPlanningInterface';
import EndOfDayPerformanceReview from './EndOfDayPerformanceReview';

const MainDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quickStats, setQuickStats] = useState(null);
  const [forecastStats, setForecastStats] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // State for modal components
  const [showSundayPlanning, setShowSundayPlanning] = useState(false);
  const [showPerformanceReview, setShowPerformanceReview] = useState(false);

  // Fetch quick stats - FIXED: No more hardcoded fallbacks
  useEffect(() => {
    const fetchQuickStats = async () => {
      try {
        const response = await api.get('/dashboard/quick_stats');
        setQuickStats(response.data);
      } catch (error) {
        console.error('Error fetching quick stats:', error);
        // Only set null on error - let the UI handle empty states properly
        setQuickStats(null);
      }
    };

    const fetchForecastStats = async () => {
      try {
        const response = await api.get('/forecast/current-week');
        setForecastStats(response.data);
      } catch (error) {
        console.error('Error fetching forecast stats:', error);
        setForecastStats(null);
      }
    };

    const fetchPendingRequests = async () => {
      try {
        const response = await api.get('/dashboard/pending_requests');
        setPendingRequests(response.data.pending_requests || 0);
      } catch (error) {
        console.error('Error fetching pending requests:', error);
        setPendingRequests(0);
      }
    };

    const fetchAllData = async () => {
      if (user) {
        setLoading(true);
        await Promise.all([
          fetchQuickStats(),
          fetchForecastStats(),
          fetchPendingRequests()
        ]);
        setLoading(false);
      }
    };

    fetchAllData();
  }, [user]);

  // Function to refresh forecast data after changes
  const refreshForecastData = async () => {
    try {
      const response = await api.get('/forecast/current-week');
      setForecastStats(response.data);
    } catch (error) {
      console.error('Error refreshing forecast data:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  // Custom Pound Icon Component
  const PoundIcon = ({ sx, ...props }) => (
    <Box sx={{ 
      fontWeight: 'bold', 
      fontSize: '1.5rem', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      width: '1em',
      height: '1em',
      color: 'inherit',
      ...sx 
    }} {...props}>
      £
    </Box>
  );

  // Custom Revenue Icon (for stats)
  const RevenueIcon = ({ sx, ...props }) => (
    <Box sx={{ 
      fontWeight: 'bold', 
      fontSize: '1.2rem', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      width: '1em',
      height: '1em',
      color: 'inherit',
      ...sx 
    }} {...props}>
      £
    </Box>
  );

  // Quick stats for header - FIXED: Proper null handling
  const headerStats = [
    {
      label: "Yesterday's Revenue",
      value: quickStats?.yesterdayRevenue ? `£${quickStats.yesterdayRevenue}` : '£0',
      change: quickStats?.yesterdayChange || 'No data',
      icon: RevenueIcon
    },
    {
      label: 'Appointments Today',
      value: quickStats?.appointmentsToday || '0',
      change: quickStats?.appointmentChange || 'No data',
      icon: Schedule
    },
    {
      label: 'Staff Working',
      value: quickStats?.staffWorking || '0',
      change: quickStats?.staffStatus || 'No data',
      icon: People
    },
    {
      label: 'Monthly Target',
      value: quickStats?.targetProgress ? `${quickStats.targetProgress}%` : '0%',
      change: quickStats?.monthlyTarget ? `of £${quickStats.monthlyTarget} goal` : 'No target set',
      icon: TrendingUp
    }
  ];

  // Main action cards - FIXED: Proper route mappings
  const mainCards = [
    {
      id: 'forecast',
      title: 'Weekly Forecast',
      subtitle: 'Plan vs Actual',
      description: 'Set weekly targets and track daily performance against forecasts. Perfect for Sunday planning sessions.',
      icon: CalendarToday,
      color: '#9c27b0',
      lightColor: '#f3e5f5',
      route: '/forecast-dashboard',  // FIXED: Matches App.js route
      stats: forecastStats ? `${forecastStats.weeklyProgress || 0}% of forecast achieved` : 'No forecast data',
      badge: forecastStats?.behindAhead === 'behind' ? '!' : null,
      quickActions: [
        { 
          label: 'This Week Performance', 
          action: () => navigate('/forecast-dashboard')
        },
        { 
          label: 'Set Next Week Forecast', 
          action: () => setShowSundayPlanning(true)
        },
        { 
          label: 'Review Completed Days', 
          action: () => setShowPerformanceReview(true)
        }
      ]
    },
    {
      id: 'team',
      title: 'My Team',
      subtitle: 'Staff & Training',
      description: 'Manage your staff, view schedules, track training, and handle reviews',
      icon: People,
      color: '#1976d2',
      lightColor: '#e3f2fd',
      route: '/employees',  // This looks correct
      stats: quickStats?.activeStaff ? `${quickStats.activeStaff} Active Staff` : 'No staff data',
      badge: pendingRequests > 0 ? pendingRequests : null,
      quickActions: [
        { label: 'View Staff', action: () => navigate('/employees') },
        { label: 'Add New Employee', action: () => navigate('/employees') },
        { label: 'Training Records', action: () => navigate('/submit_training') }  // FIXED: Actual route
      ]
    },
    {
      id: 'time',
      title: 'Time Tracking',
      subtitle: 'Schedule & Attendance',
      description: 'Hours, attendance, holidays, and absence management',
      icon: Schedule,
      color: '#388e3c',
      lightColor: '#e8f5e8',
      route: '/time-management',  // This looks correct
      stats: quickStats?.workingToday || 'No attendance data',
      quickActions: [
        { label: 'Holiday Calendar', action: () => navigate('/holiday_calendar') },  // FIXED: Actual route
        { label: 'Request Time Off', action: () => navigate('/submit_holiday_request') },  // FIXED: Actual route
        { label: 'Attendance', action: () => navigate('/time-management') }
      ]
    },
    {
      id: 'money',
      title: 'Money',
      subtitle: 'Income & Reports',
      description: 'Track daily takings, commissions, and financial reports',
      icon: PoundIcon,
      color: '#f57c00',
      lightColor: '#fff3e0',
      route: '/money',  // FIXED: Matches App.js route
      stats: quickStats?.monthlyRevenue ? `£${quickStats.monthlyRevenue}` : '£0',
      quickActions: [
        { label: 'Daily Takings', action: () => navigate('/money') },  // FIXED: Actual route
        { label: 'Commission Report', action: () => navigate('/commission/dashboard') },  // FIXED: Actual route
        { label: 'VAT Dashboard', action: () => navigate('/vat_dashboard') }  // FIXED: Actual route
      ]
    },
    {
      id: 'products',
      title: 'Products',
      subtitle: 'Stock & Supplies',
      description: 'Manage your salon products, track stock levels, and order supplies',
      icon: Inventory,
      color: '#d32f2f',
      lightColor: '#ffebee',
      route: '/stock',  // FIXED: Matches App.js route
      stats: quickStats?.totalProducts ? `${quickStats.totalProducts} Items` : 'No product data',
      quickActions: [
        { label: 'Check Stock', action: () => navigate('/stock') },  // FIXED: Actual route
        { label: 'Add Products', action: () => navigate('/stock') },  // FIXED: Actual route
        { label: 'Order Supplies', action: () => navigate('/stock/purchase-orders') }  // FIXED: Actual route
      ]
    },
    {
      id: 'reports',
      title: 'Reports',
      subtitle: 'Analytics & Insights',
      description: 'View business performance, trends, and detailed reports',
      icon: Assessment,
      color: '#0288d1',
      lightColor: '#e1f5fe',
      route: '/reports',  // FIXED: Matches App.js route
      stats: quickStats?.growthRate ? `↗ ${quickStats.growthRate}% Growth` : 'No growth data',
      quickActions: [
        { label: 'Performance Dashboard', action: () => navigate('/reports') },  // FIXED: Actual route
        { label: 'Monthly Report', action: () => navigate('/reports/profit-loss') },  // FIXED: Actual route
        { label: 'Trends', action: () => navigate('/reports') }  // FIXED: Actual route
      ]
    },
    {
      id: 'settings',
      title: 'Settings',
      subtitle: 'System & Data',
      description: 'Manage your salon settings, import data, and system preferences',
      icon: Settings,
      color: '#7b1fa2',
      lightColor: '#f3e5f5',
      route: '/settings',  // This looks correct
      stats: 'System Ready',
      quickActions: [
        { label: 'Salon Profile', action: () => navigate('/settings') },
        { label: 'Import Data', action: () => navigate('/import/test') },  // FIXED: Actual route
        { label: 'User Settings', action: () => navigate('/personal_info') }  // FIXED: Actual route
      ]
    }
  ];

  // Getting started checklist - FIXED: Dynamic completion based on real data
  const gettingStartedItems = [
    { 
      task: 'Add your first employee', 
      completed: (quickStats?.activeStaff && parseInt(quickStats.activeStaff) > 0) || false 
    },
    { 
      task: 'Set up your services & prices', 
      completed: quickStats?.servicesConfigured || false 
    },
    { 
      task: 'Set your first weekly forecast', 
      completed: forecastStats?.hasActiveForecast || false 
    },
    { 
      task: 'Book your first appointment', 
      completed: (quickStats?.appointmentsToday && parseInt(quickStats.appointmentsToday) > 0) || false 
    },
    { 
      task: 'Upload your product list', 
      completed: (quickStats?.totalProducts && parseInt(quickStats.totalProducts) > 0) || false 
    }
  ];

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6">Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Clean Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          bgcolor: 'white', 
          color: 'text.primary',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" fontWeight="bold" color="primary">
              Salon Management System
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton color="inherit">
              <Badge badgeContent={pendingRequests || 0} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            
            <Button
              onClick={handleUserMenuClick}
              startIcon={<AccountCircle />}
              sx={{ textTransform: 'none', color: 'text.primary' }}
            >
              {user?.email}
            </Button>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
            >
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/personal-info'); }}>
                Profile Settings
              </MenuItem>
              <MenuItem onClick={() => { handleUserMenuClose(); handleLogout(); }}>
                <Logout sx={{ mr: 1, fontSize: 20 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
        {/* Welcome Section */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Welcome back! 👋
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Here's what's happening in your salon today
              </Typography>
            </Box>
            
            <FormControlLabel
              control={
                <Switch
                  checked={showAdvanced}
                  onChange={(e) => setShowAdvanced(e.target.checked)}
                  color="primary"
                />
              }
              label="Advanced View"
            />
          </Box>

          {/* Quick Stats - FIXED: Proper data handling */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {headerStats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color={stat.change.includes('No data') ? 'text.secondary' : 'success.main'}>
                      {stat.change}
                    </Typography>
                    
                    {/* Add progress bar for Monthly Target */}
                    {stat.label === 'Monthly Target' && quickStats?.targetProgress && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={parseInt(quickStats.targetProgress) || 0}
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: parseInt(quickStats.targetProgress) >= 80 ? 'success.main' : 
                                              parseInt(quickStats.targetProgress) >= 60 ? 'warning.main' : 'error.main'
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  <stat.icon sx={{ fontSize: 40, color: 'text.secondary', ml: 2 }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* What would you like to do? */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
            What would you like to do?
          </Typography>
          
          <Grid container spacing={3}>
            {mainCards.map((card) => (
              <Grid item xs={12} md={6} lg={4} key={card.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    },
                    // Special styling for the forecast card
                    ...(card.id === 'forecast' && {
                      border: '2px solid',
                      borderColor: card.color,
                      position: 'relative',
                      '&::before': {
                        content: '"NEW"',
                        position: 'absolute',
                        top: -1,
                        right: -1,
                        backgroundColor: '#ff4444',
                        color: 'white',
                        padding: '4px 8px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        borderRadius: '0 4px 0 4px',
                        zIndex: 1
                      }
                    })
                  }}
                  onClick={() => navigate(card.route)}
                >
                  {/* Card Header */}
                  <Box 
                    sx={{ 
                      bgcolor: card.color,
                      color: 'white',
                      p: 3,
                      position: 'relative'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <card.icon sx={{ fontSize: 32 }} />
                        <Box>
                          <Typography variant="h5" fontWeight="bold">
                            {card.title}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {card.subtitle}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {card.badge && (
                        <Badge 
                          badgeContent={card.badge} 
                          color="error"
                          sx={{ position: 'absolute', top: 16, right: 16 }}
                        />
                      )}
                      
                      <IconButton size="small" sx={{ color: 'white', opacity: 0.7 }}>
                        <HelpOutline />
                      </IconButton>
                    </Box>
                    
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 'medium' }}>
                      {card.stats}
                    </Typography>
                  </Box>

                  {/* Card Body */}
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      {card.description}
                    </Typography>
                    
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                        Quick Actions:
                      </Typography>
                      {card.quickActions.map((action, index) => (
                        <Button
                          key={index}
                          fullWidth
                          variant="text"
                          sx={{
                            justifyContent: 'space-between',
                            mb: 1,
                            bgcolor: card.lightColor,
                            color: card.color,
                            '&:hover': {
                              bgcolor: card.lightColor,
                              opacity: 0.8
                            }
                          }}
                          endIcon={<ArrowForward />}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click
                            if (action.action) {
                              action.action();
                            } else {
                              navigate(card.route);
                            }
                          }}
                        >
                          {action.label || action}
                        </Button>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Getting Started Help */}
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
            <Box sx={{ 
              bgcolor: 'primary.light', 
              p: 2, 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <HelpOutline sx={{ fontSize: 32, color: 'primary.main' }} />
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Need Help Getting Started?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                New to the system? Here are some common tasks to help you get started:
              </Typography>
              
              <Grid container spacing={2}>
                {gettingStartedItems.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 2,
                      cursor: item.task.includes('forecast') ? 'pointer' : 'default'
                    }}
                    onClick={item.task.includes('forecast') ? () => setShowSundayPlanning(true) : undefined}
                    >
                      <CheckCircle 
                        sx={{ 
                          color: item.completed ? 'success.main' : 'grey.400',
                          fontSize: 20 
                        }} 
                      />
                      <Typography 
                        variant="body2"
                        sx={{ 
                          textDecoration: item.completed ? 'line-through' : 'none',
                          color: item.completed ? 'text.secondary' : 'text.primary'
                        }}
                      >
                        {item.task}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Paper>

        {/* Footer Tip */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            💡 Hover over the <HelpOutline sx={{ fontSize: 16, verticalAlign: 'middle' }} /> icons for helpful tips!
          </Typography>
        </Box>
      </Box>

      {/* Modal Components */}
      {showSundayPlanning && (
        <SundayPlanningInterface
          onClose={() => setShowSundayPlanning(false)}
          onSave={() => {
            refreshForecastData();
            setShowSundayPlanning(false);
          }}
        />
      )}

      {showPerformanceReview && (
        <EndOfDayPerformanceReview
          onClose={() => setShowPerformanceReview(false)}
        />
      )}
    </Box>
  );
};

export default MainDashboard;